/**
 * Release notes for the signed-in What's New screen.
 * Copied from app/src/main/assets/mockloc-release-history.json.
 * Replace this module when the release API is connected.
 * Each block on the screen already has a skeleton for that wiring.
 */
import history from './mockloc-release-history.json';

export type NoteType = 'new_feature' | 'ui_enhancement' | 'improvement' | 'bug_fix' | 'crash';

export type ChangeFilter = 'all' | 'features' | 'enhancements' | 'fixes';

export type GroupTone = 'feature' | 'enhance' | 'improve' | 'fix' | 'crash';

export type SpotlightTone = 'playlist' | 'backup' | 'coins' | 'general';

export interface ReleaseItem {
  id: string;
  type: NoteType;
  title: string;
  detail: string;
  image?: string;
}

export interface ReleaseCounts {
  features: number;
  enhancements: number;
  improvements: number;
  fixes: number;
  crashes: number;
  total: number;
}

export interface ReleaseRecord {
  version: string;
  title: string;
  headline: string;
  summary: string;
  dateLabel: string;
  isCurrent: boolean;
  updateLabel: string;
  items: ReleaseItem[];
  counts: ReleaseCounts;
}

export interface SpotlightCard {
  id: string;
  title: string;
  kicker: string;
  blurb: string;
  tone: SpotlightTone;
  image?: string;
}

export interface ChangeGroup {
  id: string;
  title: string;
  meta: string;
  tone: GroupTone;
  items: ReleaseItem[];
}

export interface StatPill {
  id: string;
  label: string;
  tone: GroupTone;
}

const NOTE_TYPES: NoteType[] = ['new_feature', 'ui_enhancement', 'improvement', 'bug_fix', 'crash'];

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const MONTHS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];

const HEADLINES: Record<string, string> = {
  '1.1.15': 'More control. Safer backups. Smoother playlists.',
};

const SPOTLIGHT_ORDER = ['Loop playlist', 'Google Drive Backup', 'Mock Coins'];

const SPOTLIGHT_COPY: Record<string, { kicker: string; blurb: string; tone: SpotlightTone }> = {
  'Loop playlist': {
    kicker: 'Playlists',
    blurb: 'Automatically replay your route from the first point when the last stop finishes.',
    tone: 'playlist',
  },
  'Google Drive Backup': {
    kicker: 'Sync & Backup',
    blurb: 'Keep mocks, playlists, itineraries, and settings backed up to your own Google Drive.',
    tone: 'backup',
  },
  'Mock Coins': {
    kicker: 'New',
    blurb: 'A simple in-app balance for MockLoc actions.',
    tone: 'coins',
  },
};

const MORE_ORDER = [
  'Device sync and device limits',
  'Forgot password + refreshed sign-in',
  'Home screen widgets for recent mocks',
  'Add to playlist from stationary point',
];

const GROUP_DEFS: Array<{
  id: string;
  title: string;
  types: NoteType[];
  filters: ChangeFilter[];
  tone: GroupTone;
  one: string;
  many: string;
}> = [
  {
    id: 'features',
    title: 'New features',
    types: ['new_feature'],
    filters: ['all', 'features'],
    tone: 'feature',
    one: 'new feature',
    many: 'new features',
  },
  {
    id: 'enhancements',
    title: 'UI enhancements',
    types: ['ui_enhancement'],
    filters: ['all', 'enhancements'],
    tone: 'enhance',
    one: 'UI enhancement',
    many: 'UI enhancements',
  },
  {
    id: 'improvements',
    title: 'Improvements',
    types: ['improvement'],
    filters: ['all', 'enhancements'],
    tone: 'improve',
    one: 'improvement',
    many: 'improvements',
  },
  {
    id: 'fixes',
    title: 'Bug fixes',
    types: ['bug_fix'],
    filters: ['all', 'fixes'],
    tone: 'fix',
    one: 'bug fix',
    many: 'bug fixes',
  },
  {
    id: 'crashes',
    title: 'Crash fixes',
    types: ['crash'],
    filters: ['all', 'fixes'],
    tone: 'crash',
    one: 'crash fix',
    many: 'crash fixes',
  },
];

export const CHANGE_FILTERS: { id: ChangeFilter; label: string }[] = [
  { id: 'all', label: 'All changes' },
  { id: 'features', label: 'Features' },
  { id: 'enhancements', label: 'Enhancements' },
  { id: 'fixes', label: 'Fixes' },
];

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

function asNoteType(value: string): NoteType | null {
  return NOTE_TYPES.indexOf(value as NoteType) >= 0 ? (value as NoteType) : null;
}

function monthIndex(name: string): number {
  const lower = name.toLowerCase();
  return MONTHS.findIndex((month) => month.startsWith(lower));
}

function formatIsoDate(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return iso;
  const month = SHORT_MONTHS[Number(match[2]) - 1];
  if (!month) return iso;
  return `${Number(match[3])} ${month} ${match[1]}`;
}

function dateLabelFor(when: string, updated: string, isCurrent: boolean): string {
  if (isCurrent && /will update/i.test(when) && updated) {
    return formatIsoDate(updated);
  }
  const named = /^([A-Za-z]+)\s+(\d{1,2})\s+(\d{4})$/.exec(when.trim());
  if (named) {
    const index = monthIndex(named[1]);
    if (index >= 0) return `${Number(named[2])} ${SHORT_MONTHS[index]} ${named[3]}`;
  }
  const dayFirst = /^(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{4})$/.exec(when.trim());
  if (dayFirst) {
    const index = monthIndex(dayFirst[2]);
    if (index >= 0) return `${Number(dayFirst[1])} ${SHORT_MONTHS[index]} ${dayFirst[3]}`;
  }
  return when.trim();
}

function summaryFor(total: number): string {
  return `${total} updates across features, UI, fixes, and stability to make MockLoc more powerful and reliable.`;
}

function noteImage(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!/^https:\/\/\S+$/i.test(trimmed)) return undefined;
  return trimmed;
}

function buildItems(
  version: string,
  items: Array<{ type: string; what: string; why: string; image?: string }>
): ReleaseItem[] {
  const notes: ReleaseItem[] = [];
  items.forEach((item, index) => {
    const type = asNoteType(item.type);
    if (!type) return;
    const image = noteImage(item.image);
    notes.push({
      id: `${version}-${index}`,
      type,
      title: item.what,
      detail: item.why,
      ...(image ? { image } : {}),
    });
  });
  return notes;
}

function countTypes(items: ReleaseItem[]): ReleaseCounts {
  return {
    features: items.filter((item) => item.type === 'new_feature').length,
    enhancements: items.filter((item) => item.type === 'ui_enhancement').length,
    improvements: items.filter((item) => item.type === 'improvement').length,
    fixes: items.filter((item) => item.type === 'bug_fix').length,
    crashes: items.filter((item) => item.type === 'crash').length,
    total: items.length,
  };
}

function buildRelease(
  entry: (typeof history.releases)[number],
  updated: string,
  current: string
): ReleaseRecord {
  const items = buildItems(entry.version, entry.items);
  const isCurrent = entry.version === current;
  return {
    version: entry.version,
    title: entry.title,
    headline: HEADLINES[entry.version] ?? entry.title,
    summary: summaryFor(items.length),
    dateLabel: dateLabelFor(entry.when, updated, isCurrent),
    isCurrent,
    updateLabel: plural(items.length, 'update', 'updates'),
    items,
    counts: countTypes(items),
  };
}

export const RELEASES: ReleaseRecord[] = history.releases.map((entry) =>
  buildRelease(entry, history.updated, history.current)
);

const currentRelease = RELEASES.find((release) => release.version === history.current) ?? RELEASES[0];

if (!currentRelease) {
  throw new Error('Release history is empty.');
}

export const CURRENT_RELEASE: ReleaseRecord = currentRelease;

/** Hero image for the latest release. Loop playlist is the first image in the current notes. */
export const HERO_IMAGE: string | undefined =
  CURRENT_RELEASE.items.find((item) => item.title === 'Loop playlist')?.image ??
  CURRENT_RELEASE.items.find((item) => item.image)?.image;

export function releaseByVersion(version: string): ReleaseRecord {
  return RELEASES.find((release) => release.version === version) ?? CURRENT_RELEASE;
}

function firstSentence(text: string): string {
  const match = text.match(/^[\s\S]*?[.!](?:\s|$)/);
  return (match ? match[0] : text).trim();
}

function fallbackSpotlight(type: NoteType): { kicker: string; tone: SpotlightTone } {
  switch (type) {
    case 'new_feature':
      return { kicker: 'New', tone: 'general' };
    case 'ui_enhancement':
      return { kicker: 'UI', tone: 'general' };
    case 'improvement':
      return { kicker: 'Improve', tone: 'general' };
    case 'bug_fix':
      return { kicker: 'Fix', tone: 'general' };
    case 'crash':
      return { kicker: 'Stability', tone: 'general' };
    default:
      return { kicker: 'Update', tone: 'general' };
  }
}

export function spotlightFor(release: ReleaseRecord): SpotlightCard[] {
  const named = SPOTLIGHT_ORDER.map((title) => release.items.find((item) => item.title === title)).filter(
    (item): item is ReleaseItem => Boolean(item)
  );
  const features = release.items.filter((item) => item.type === 'new_feature');
  const pool = features.length > 0 ? features : release.items;
  const chosen = named.length >= 2 ? named : pool.slice(0, 3);
  return chosen.map((item) => {
    const copy = SPOTLIGHT_COPY[item.title];
    const fallback = fallbackSpotlight(item.type);
    return {
      id: item.id,
      title: item.title,
      kicker: copy?.kicker ?? fallback.kicker,
      blurb: copy?.blurb ?? firstSentence(item.detail),
      tone: copy?.tone ?? fallback.tone,
      ...(item.image ? { image: item.image } : {}),
    };
  });
}

export function moreFeaturesFor(release: ReleaseRecord, spotlightIds: string[]): ReleaseItem[] {
  const used = new Set(spotlightIds);
  const features = release.items.filter((item) => item.type === 'new_feature' && !used.has(item.id));
  const preferred = MORE_ORDER.map((title) => features.find((item) => item.title === title)).filter(
    (item): item is ReleaseItem => Boolean(item)
  );
  const preferredIds = new Set(preferred.map((item) => item.id));
  const rest = features.filter((item) => !preferredIds.has(item.id));
  return [...preferred, ...rest].slice(0, 4);
}

export function hiddenFeatureCount(release: ReleaseRecord, shownIds: string[]): number {
  const shown = new Set(shownIds);
  return release.items.filter((item) => item.type === 'new_feature' && !shown.has(item.id)).length;
}

export function itemMatches(item: ReleaseItem, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return `${item.title} ${item.detail}`.toLowerCase().includes(q);
}

function releaseMatches(release: ReleaseRecord, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [release.version, `v${release.version}`, release.title, release.headline, release.dateLabel]
    .join(' ')
    .toLowerCase();
  if (haystack.includes(q)) return true;
  return release.items.some((item) => itemMatches(item, q));
}

export function otherReleases(version: string, query: string): ReleaseRecord[] {
  return RELEASES.filter((release) => release.version !== version && releaseMatches(release, query));
}

export function groupsFor(release: ReleaseRecord, filter: ChangeFilter, query: string): ChangeGroup[] {
  return GROUP_DEFS.filter((def) => def.filters.includes(filter))
    .map((def) => {
      const items = release.items.filter((item) => def.types.includes(item.type) && itemMatches(item, query));
      return {
        id: def.id,
        title: def.title,
        tone: def.tone,
        meta: plural(items.length, def.one, def.many),
        items,
      };
    })
    .filter((group) => group.items.length > 0);
}

export function tabCounts(release: ReleaseRecord, query = ''): Record<ChangeFilter, number> {
  const items = release.items.filter((item) => itemMatches(item, query));
  return {
    all: items.length,
    features: items.filter((item) => item.type === 'new_feature').length,
    enhancements: items.filter((item) => item.type === 'ui_enhancement' || item.type === 'improvement').length,
    fixes: items.filter((item) => item.type === 'bug_fix' || item.type === 'crash').length,
  };
}

export function statPills(release: ReleaseRecord): StatPill[] {
  const pills: StatPill[] = [];
  const { features, enhancements, improvements, fixes, crashes } = release.counts;
  if (features) pills.push({ id: 'features', tone: 'feature', label: plural(features, 'New feature', 'New features') });
  if (enhancements) {
    pills.push({
      id: 'enhancements',
      tone: 'enhance',
      label: plural(enhancements, 'UI enhancement', 'UI enhancements'),
    });
  }
  if (improvements) {
    pills.push({ id: 'improvements', tone: 'improve', label: plural(improvements, 'Improvement', 'Improvements') });
  }
  if (fixes) pills.push({ id: 'fixes', tone: 'fix', label: plural(fixes, 'Bug fix', 'Bug fixes') });
  if (crashes) pills.push({ id: 'crashes', tone: 'crash', label: plural(crashes, 'Crash fix', 'Crash fixes') });
  return pills;
}

export function emptyNotesMessage(query: string, filter: ChangeFilter, version: string): string {
  if (query.trim()) return 'No updates match that search.';
  if (filter === 'features') return `No features in v${version}.`;
  if (filter === 'enhancements') return `No enhancements in v${version}.`;
  if (filter === 'fixes') return `No fixes in v${version}.`;
  return `v${version} has no notes yet.`;
}
